import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { common, input, overview } from "../../../../../i18n";
import ArrayUtils from "../../../../../utils/arrayUtils";
import { InputReactHookForm } from "../../../../../components/input/InputReactHookForm";
import { CustomAutoComplete } from "../../../../../components/input/CustomAutoComplete";
import { useAuth } from "../../../../../contexts/UserContext";
import {
  AddEmissionContext,
  FormValuesStepOne,
} from "../../../../../contexts/AddEmissionContext";

const AddEmissionStepOne: React.FC = () => {
  const { t, i18n } = useTranslation([common, overview, input], {
    nsMode: "fallback",
  });

  const user = useAuth();

  const navigate = useNavigate();

  const { setValue, control, watch, setError, clearErrors, getValues } =
    useFormContext<FormValuesStepOne>();

  // 1. ดึงตัวแปรที่จำเป็นจาก Context
  const { emissionGroupList, subOrganizationList, assetList } =
    AddEmissionContext();

  // 2. ใช้ Watch เพื่อติดตามค่าที่เปลี่ยนไปแบบ Real-time
  const subOrganizations = watch("subOrganizations");
  const emissionGroup = watch("emissionGroup");
  const assetType = watch("assetType");
  const assetName = watch("assetName");
  const site = watch("site");
  const location = watch("location");

  // 3. ระบบความจำ "ป้องกันผีหลอก" (ห้าม Auto-select ถ้าตั้งใจลบทิ้ง)
  const clearedFields = useRef<Set<string>>(new Set());

  // ----------------------------------------------------------------------
  // 🔄 LOGIC การเปลี่ยนค่าและล้างช่องที่เกี่ยวข้องกัน
  // ----------------------------------------------------------------------
  const changeValue = (event: any, fieldName: string) => {
    const isCleared = event.target.value === null;

    const value = event.target.value; // 💡 ดึงค่าออกมาเก็บในตัวแปรให้ใช้ง่ายๆ

    if (isCleared) {
      clearedFields.current.add(fieldName);
    } else {
      clearedFields.current.delete(fieldName);
    }

    // ล้างเฉพาะค่าของลูกๆ ที่เกี่ยวข้องกันเมื่อพ่อแม่เปลี่ยน

    switch (fieldName) {
      case "subOrganizations":
        if (!isCleared) {
          clearedFields.current.delete("site");
          clearedFields.current.delete("assetType");
          clearedFields.current.delete("assetName");
        } else {
          clearedFields.current.add("site");
          clearedFields.current.add("assetType");
          clearedFields.current.add("assetName");
        }
        // 🚨 เติม Option ให้ทุกตัว
        setValue("site", "", { shouldValidate: true, shouldDirty: true });
        setValue("assetType", "", { shouldValidate: true, shouldDirty: true });
        setValue("assetName", "", { shouldValidate: true, shouldDirty: true });
        break;

      case "site":
      case "emissionGroup":
        if (!isCleared) {
          clearedFields.current.delete("assetType");
          clearedFields.current.delete("assetName");
        } else {
          clearedFields.current.add("assetType");
          clearedFields.current.add("assetName");
        }
        // 🚨 เติม Option ให้ทุกตัว
        setValue("assetType", "", { shouldValidate: true, shouldDirty: true });
        setValue("assetName", "", { shouldValidate: true, shouldDirty: true });
        break;

      case "assetType":
        if (!isCleared) {
          clearedFields.current.delete("assetName");
        } else {
          clearedFields.current.add("assetName");
        }
        setValue("assetName", "", { shouldValidate: true, shouldDirty: true });
        break;

      case "assetName":
        if (!isCleared && value !== "add") {
          // 1. ค้นหาข้อมูล Asset เต็มๆ จาก assetList ด้วย ID ที่ User เลือก
          const selectedAsset = assetList.find((a) => a._id === value);

          if (selectedAsset) {
            const currentEmissionGroup = getValues("emissionGroup");
            const currentAssetType = getValues("assetType");

            // 2. ถ้าช่อง Emission Group ยังว่างอยู่ (หรือค่าไม่ตรง) ให้ Auto-set เลย!
            if (
              !currentEmissionGroup ||
              currentEmissionGroup !== selectedAsset.emissionGroup
            ) {
              setValue("emissionGroup", selectedAsset.emissionGroup, {
                shouldValidate: true,
                shouldDirty: true,
              });
              clearedFields.current.delete("emissionGroup"); // ลบออกจากบัญชีดำ ป้องกันผีหลอก
            }

            // 3. ถ้าช่อง Asset Type ยังว่างอยู่ (หรือค่าไม่ตรง) ให้ Auto-set ด้วย!
            if (
              !currentAssetType ||
              currentAssetType !== selectedAsset.assetType
            ) {
              setValue("assetType", selectedAsset.assetType, {
                shouldValidate: true,
                shouldDirty: true,
              });
              clearedFields.current.delete("assetType"); // ลบออกจากบัญชีดำ ป้องกันผีหลอก
            }
          }
        }
        break;
      default:
        break;
    }
  };

  // ----------------------------------------------------------------------
  // 🔍 LOGIC การกรองข้อมูลเมนูแบบ Real-time (Derived State)
  // ----------------------------------------------------------------------

  // กรอง Site
  const selectedSubOrg = subOrganizationList.find(
    (i) => i.id === subOrganizations,
  );
  const siteMenu =
    selectedSubOrg?.sites.map((s) => ({
      nameEN: s.name,
      nameTH: s.name,
      value: s.refId,
    })) ?? [];

  // กรอง Asset แบบรวมศูนย์ (ตัด Loop ทิ้ง)
  let filteredAssets = [...assetList];

  if (subOrganizations) {
    filteredAssets = filteredAssets.filter(
      (i) => i.suborganizationName === selectedSubOrg?.name,
    );
  }
  if (site) {
    const siteName = selectedSubOrg?.sites.find((s) => s.refId === site)?.name;
    filteredAssets = filteredAssets.filter((i) => i.siteName === siteName);
  }
  if (emissionGroup) {
    filteredAssets = filteredAssets.filter(
      (i) => i.emissionGroup === emissionGroup,
    );
  }

  // ดึง Asset Type ออกมาเป็นตัวเลือก
  const rawAssetTypeMenu = ArrayUtils.filterArrayUniqueByKey(
    "assetType",
    filteredAssets,
  ).map((m) => ({
    value: m.assetType,
    nameEN: t(`${m.assetType}`),
    nameTH: t(`${m.assetType}`),
  }));

  const finalAssetTypeMenu =
    rawAssetTypeMenu.length > 0
      ? rawAssetTypeMenu
      : user.isAdmin()
      ? [{ value: "add", nameEN: t("Add-Asset"), nameTH: t("Add-Asset") }]
      : [];

  // ดึง Asset Name ออกมาเป็นตัวเลือก
  const filteredAssetNames = assetType
    ? filteredAssets.filter((i) => i.assetType === assetType)
    : filteredAssets;

  const rawAssetNameMenu = filteredAssetNames.map((m) => ({
    value: m._id,
    nameEN: t(`${m.name}`),
    nameTH: t(`${m.name}`),
  }));

  const finalAssetNameMenu =
    rawAssetNameMenu.length > 0
      ? rawAssetNameMenu
      : user.isAdmin()
      ? [{ value: "add", nameEN: t("Add-Asset"), nameTH: t("Add-Asset") }]
      : [];

  // ----------------------------------------------------------------------
  // ⚡ AUTO-SELECT & EFFECTS
  // ----------------------------------------------------------------------

  // ฟังก์ชันช่วย Auto-select
  const autoSelectField = (menu: any[], fieldName: any, currentValue: any) => {
    if (
      menu.length === 1 &&
      !currentValue && // ใช้ค่าจาก watch แทน getValues จะปลอดภัยกว่า
      !clearedFields.current.has(fieldName) &&
      menu[0].value !== "add"
    ) {
      setValue(fieldName, menu[0].value, { shouldValidate: true });
    }
  };

  // 1. ตั้งค่าเริ่มต้นเมื่อเปิดฟอร์ม
  useEffect(() => {
    setValue(
      "organization",
      user.organization != null ? user.organization.name : "",
    );

    if (subOrganizationList.length === 1 && !getValues("subOrganizations")) {
      setValue("subOrganizations", subOrganizationList[0].id);
    }
  }, [subOrganizationList, user.organization]);

  // 2. จัดการ Auto-select และ Location Text
  useEffect(() => {
    autoSelectField(siteMenu, "site", site);
    autoSelectField(rawAssetTypeMenu, "assetType", assetType);
    autoSelectField(rawAssetNameMenu, "assetName", assetName);

    // อัปเดตช่อง Location เมื่อ Site เปลี่ยน
    if (site) {
      const findSite = selectedSubOrg?.sites.find((s) => s.refId === site);
      const lang = i18n.language === "th" ? "th" : "en";
      const loc = findSite?.location;
      const resolve = (field: { en: string | null; th: string | null } | string | null | undefined) => {
        if (!field) return "";
        if (typeof field === "string") return field;
        return field[lang] ?? field.en ?? "";
      };
      setValue(
        "location",
        `${resolve(loc?.address)} ${resolve(loc?.district)} ${resolve(loc?.city)}`.trim(),
      );
    } else {
      setValue("location", "");
    }
  }, [subOrganizations, site, emissionGroup, assetType, assetName]);

  // 3. นำทางไปหน้าสร้าง Asset ถ้ากด Add
  useEffect(() => {
    if (assetType === "add" || assetName === "add") {
      navigate("/setting/business-structure/asset");
    }
  }, [assetType, assetName, navigate]);

  // 4. ดักจับ Error กรณีไม่มี Asset (แสดงคำเตือนใต้ Input)
  useEffect(() => {
    if (subOrganizations && site && emissionGroup) {
      if (rawAssetTypeMenu.length === 0) {
        setError("assetType", {
          message: user.isAdmin()
            ? `${t("Asset_Not_Found")}`
            : `${t("Asset-Not-Found-User")}`,
        });
      } else {
        clearErrors("assetType");
      }

      if (rawAssetNameMenu.length === 0 && assetType) {
        setError("assetName", {
          message: user.isAdmin()
            ? `${t("Asset_Not_Found")}`
            : `${t("Asset-Not-Found-User")}`,
        });
      } else {
        clearErrors("assetName");
      }
    }
  }, [
    rawAssetTypeMenu.length,
    rawAssetNameMenu.length,
    subOrganizations,
    site,
    emissionGroup,
    assetType,
  ]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <InputReactHookForm
          label={t("Organization")}
          info={t("Organization")}
          name="organization"
          control={control}
          placeholder=""
          defaultValue={""}
          disabled
          required
        />

        <CustomAutoComplete
          required
          rules={{
            required: t("Please-Select-Organization"),
            onChange: (event) => changeValue(event, "subOrganizations"),
          }}
          name="subOrganizations"
          control={control}
          label={t("Sub-Organizations")}
          info={t("Sub-Organizations")}
          placeholder={t("Select-Param", {
            param: t("Sub-Organizations"),
          })}
          listMenuItem={subOrganizationList.map((m) => ({
            value: m.id,
            nameEN: t(`${m.name}`),
            nameTH: t(`${m.name}`),
            disabled: false,
          }))}
          defaultValue={""}
        />

        <CustomAutoComplete
          required
          rules={{
            required: t("Site"),
            onChange: (event) => changeValue(event, "site"),
          }}
          name="site"
          control={control}
          label={t("Site")}
          info={t("Site")}
          placeholder={t("Select-Param", {
            param: t("Site"),
          })}
          listMenuItem={siteMenu}
          disabled={!subOrganizations}
          defaultValue={""}
        />

        <InputReactHookForm
          label={t("Location")}
          info={t("Location")}
          name="location"
          control={control}
          placeholder={location}
          defaultValue={""}
          disabled
          required
        />

        <CustomAutoComplete
          required
          rules={{
            required: t("This-field-is-required", {
              param: t("Emission-Group"),
            }),
            onChange: (event) => changeValue(event, "emissionGroup"),
          }}
          name="emissionGroup"
          control={control}
          label={t("Emission-Group")}
          info={t("Emission-Group")}
          placeholder={t("Select-Param", {
            param: t("Emission-Group"),
          })}
          listMenuItem={emissionGroupList.map((m) => ({
            value: m.emissionGroup,
            nameEN: t(`${m.groupEn}`),
            nameTH: t(`${m.groupEn}`),
            disabled: m.disabled ? true : false,
          }))}
          disabled={!site}
        />

        <CustomAutoComplete
          required
          rules={{
            required: t("This-field-is-required", {
              param: t("Asset-Type"),
            }),
            onChange: (event) => changeValue(event, "assetType"),
          }}
          name="assetType"
          control={control}
          label={t("Asset-Type")}
          info={t("Asset-Type-Description")}
          placeholder={t("Select-Param", {
            param: t("Asset-Type"),
          })}
          listMenuItem={finalAssetTypeMenu}
          disabled={!site}
          defaultValue={assetType ?? ""}
        />

        <CustomAutoComplete
          name="assetName"
          rules={{
            required: t("This-field-is-required", {
              param: t("Asset-Name"),
            }),
            onChange: (event) => changeValue(event, "assetName"),
          }}
          required
          control={control}
          label={t("Asset-Name")}
          info={t("Asset-Name-Description")}
          placeholder={t("Select-Param", {
            param: t("Asset-Name"),
          })}
          listMenuItem={finalAssetNameMenu}
          disabled={!site}
          defaultValue={assetName ?? ""}
        />
      </Box>
    </>
  );
};

export default AddEmissionStepOne;
